import {
  Box,
  Button,
  Typography,
  makeStyles,
  useFormControl,
  useTheme,
} from '@material-ui/core';
import { CSSProperties, FC, Ref, forwardRef } from 'react';

import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { WbDivider, WbDividerProps } from '../WbDivider';

import clsx from 'clsx';
import {
  WbTextField,
  WbTextFieldProps,
  AccessibilityProps,
} from './WbTextField';
import { ClearButton } from './ClearButton';

const useUrnFieldStyles = makeStyles(theme => ({
  adornment: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  prefix: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingInline: theme.spacing(1.5),
  },
  version: {
    height: '100%',
    display: 'flex',
    minWidth: 40,
    alignItems: 'center',
    justifyContent: 'center',
    paddingInline: theme.spacing(1.5),
  },
  versionButtonsContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  clearButton: {
    marginRight: theme.spacing(1),
  },
  versionButton: {
    flex: 1,
    fontSize: theme.spacing(1.5),
    borderRadius: 0,
    minHeight: 0,
    minWidth: 0,
    width: theme.spacing(3),
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.grey[50],
  },
  nonInteractive: {
    backgroundColor: theme.palette.grey[50],
  },
}));
export type WbUrnValue = { editableSegment: string; version: number };

type UrnProps = {
  prefix: string;
  defaultVersion?: number;
  urnValue?: WbUrnValue;
  onUrnChange: (val: WbUrnValue) => void;
};

export type WbUrnFieldProps = Omit<
  WbTextFieldProps,
  'value' | 'onChange' | 'debounceMs' | 'startAdornment' | 'endAdornment'
> &
  AccessibilityProps &
  UrnProps;

const WbUrnFieldDivider: FC<WbDividerProps> = ({
  orientation = 'vertical',
  ...props
}) => {
  const theme = useTheme();

  const control = useFormControl();

  const getStyle = () => {
    const style: CSSProperties = {};
    style.backgroundColor = theme.palette.grey[300];
    if (control?.focused) style.backgroundColor = theme.palette.primary.main;
    if (control?.error) style.backgroundColor = theme.palette.error.main;

    if (orientation === 'vertical') style.height = '100%';
    return style;
  };
  return <WbDivider orientation={orientation} {...props} style={getStyle()} />;
};

const WbUrnFieldStartAdornment: FC<WbUrnFieldProps> = ({ prefix }) => {
  const classes = useUrnFieldStyles();

  return (
    <Box className={classes.adornment}>
      <Box className={clsx(classes.prefix, classes.nonInteractive)}>
        <Typography>{prefix}</Typography>
      </Box>
      <WbUrnFieldDivider />
    </Box>
  );
};

const WbUrnFieldEndAdornment: FC<WbUrnFieldProps> = ({
  clearable,
  urnValue,
  onUrnChange,
  defaultVersion,
  ...props
}) => {
  const classes = useUrnFieldStyles();

  const versionInputDisabled = !urnValue;

  return (
    <>
      {clearable && !props.disabled && (
        <ClearButton
          className={classes.clearButton}
          onClear={() => {
            onUrnChange({
              version: defaultVersion ?? 0,
              ...urnValue,
              editableSegment: '',
            });
          }}
        />
      )}
      <Box className={classes.adornment}>
        <WbUrnFieldDivider />
        <Box
          className={clsx(
            classes.version,
            versionInputDisabled && classes.nonInteractive,
          )}
        >
          <Typography>:{urnValue?.version ?? defaultVersion ?? 0}</Typography>
        </Box>
        <WbUrnFieldDivider />
        <Box className={clsx(classes.versionButtonsContainer)}>
          <Button
            className={classes.versionButton}
            type="button"
            title="increment version"
            disabled={versionInputDisabled}
            onClick={() => {
              if (!urnValue) return;
              onUrnChange({ ...urnValue, version: urnValue.version + 1 });
            }}
          >
            <AddIcon fontSize="inherit" />
          </Button>
          <WbUrnFieldDivider orientation="horizontal" />
          <Button
            className={classes.versionButton}
            disabled={versionInputDisabled || urnValue.version <= 0}
            type="button"
            title="decrease version"
            onClick={() => {
              if (!urnValue) return;
              onUrnChange({ ...urnValue, version: urnValue.version - 1 });
            }}
          >
            <RemoveIcon fontSize="inherit" />
          </Button>
        </Box>
      </Box>
    </>
  );
};

export const WbUrnField = forwardRef(
  (props: WbUrnFieldProps, ref?: Ref<any>) => {
    const {
      prefix,
      defaultVersion,
      urnValue,
      onUrnChange,
      InputProps,
      clearable,
      id,
      label,
      ariaLabel,
      ...otherProps
    } = props;

    const theme = useTheme();

    const accessibilityProps =
      id && label ? { id, label } : { id, ariaLabel: ariaLabel ?? 'urn' };

    return (
      <WbTextField
        inputRef={ref}
        InputProps={{
          inputProps: {
            style: { paddingInline: theme.spacing(1.5) },
            value: urnValue?.editableSegment ?? '',
            onChange: e =>
              onUrnChange({
                version: defaultVersion ?? 0,
                ...urnValue,
                editableSegment: e.currentTarget.value,
              }),
          },
          startAdornment: <WbUrnFieldStartAdornment {...props} />,
          endAdornment: <WbUrnFieldEndAdornment {...props} />,
          style: { padding: 0 },
          ...InputProps,
        }}
        {...otherProps}
        {...accessibilityProps}
      />
    );
  },
);

WbUrnField.displayName = 'WbUrnField';
